import type { FunctionComponent, HTMLAttributes, MouseEvent } from 'react';
import { useRef, useState } from 'react';

import { IconButton, Icons } from '../../controls/iconButton/IconButton';
import { RangeInput } from '../../controls/rangeInput/RangeInput';
import { Tooltip, type TooltipProps } from '../../tooltip/Tooltip';

import styles from './navRangeSlider.module.css';

export interface NavRangeSliderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'onDrag'> {
  iconOn: keyof Icons;
  iconOff: keyof Icons;
  value: number;
  onChange: (value: number) => void;
  onDrag: (value: number) => void;
  onToggle: (event: MouseEvent | undefined) => void;
  min: number;
  max: number;
  step: number;
  tooltipOptions?: TooltipProps;
}

export const NavRangeSlider: FunctionComponent<NavRangeSliderProps> = ({
  iconOn,
  iconOff,
  value,
  onChange,
  onDrag,
  onToggle,
  min,
  max,
  step,
  tooltipOptions = { text: '', disabled: true },
  ...rest
}) => {
  const handleRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isOutside = useRef<boolean>(true);

  // Tooltip will obstruct the range slider when pointer is on top of this
  // component, so it needs to be hidden.
  const [showTooltip, setShowTooltip] = useState(!tooltipOptions.disabled);
  const updatedTooltipOpts = {
    ...tooltipOptions,
    disabled: tooltipOptions.disabled || !showTooltip,
  };

  const blur = (): void => {
    if (!(isOutside.current && !isDragging)) return;
    if (handleRef.current) {
      handleRef.current.blur();
    }
    if (toggleRef.current) {
      toggleRef.current.blur();
    }
  };

  const handlePointerLeave = (): void => {
    isOutside.current = false;
    setShowTooltip(true);
  };

  const handlePointerEnter = (): void => {
    isOutside.current = true;
    setShowTooltip(false);
    blur();
  };

  return (
    <div
      data-testid="nav-range-slider"
      className={styles.navRangeSlider}
      {...rest}
      onPointerLeave={handlePointerLeave}
      onPointerEnter={handlePointerEnter}
    >
      <div className={styles.navRangeSlider__iconWrapper}>
        <Tooltip targetRef={toggleRef} {...updatedTooltipOpts}>
          <IconButton icon={value ? iconOn : iconOff} ghost onClick={onToggle} ref={toggleRef} />
        </Tooltip>
      </div>
      <div className={styles.navRangeSlider__sliderWrapper}>
        <RangeInput
          data-testid="nav-range-slider-input"
          ref={handleRef}
          value={value}
          onChange={onChange}
          onDrag={onDrag}
          min={min}
          max={max}
          step={step}
          vertical
          onDragStart={(): void => setIsDragging(true)}
          onDragEnd={(): void => {
            setIsDragging(false);
            blur();
          }}
          onKeyDown={(event): void => {
            if (event.key === 'Escape') {
              blur();
            }
          }}
        />
      </div>
    </div>
  );
};
